'use strict';
import { deleteChatBookmark, getChatBookmarks } from '@api/aiAnalyzer.js';
import Search from '@components/Common/Search.js';
import toast from '@components/Common/Toast.js';
import Modal from '@components/Modals/Modal.js';
import { TrashIcon } from '@icons/index.js';
import { UIButton, UIDiv, UIText } from '@ui';
import { getDateDisplay } from '@utils/dateUtils.js';
class AiAnalyzerBookmarks extends UIDiv {
  constructor(copyBookmark) {
    super();
    this.setClass('flex flex-col h-full');
    this.bookmarks = [];
    this.isLoaded = false;
    this.copyBookmark = copyBookmark;
    this.searchQuery = '';
    this.#buildComponent();
  }

  async loadBookmarks() {
    if (this.isLoaded) {
      this.#updateBookmarksList();
      return;
    }

    try {
      const response = await getChatBookmarks();
      this.bookmarks = response.body;
      this.isLoaded = true;
      this.#updateBookmarksList();
    } catch (error) {
      console.error('Error loading bookmarks:', error);
      toast.error(
        i18next.t('sidebar.aiAnalyzer.toast.failed_to_refresh_bookmarks'),
      );
    }
  }

  async refreshBookmarks() {
    try {
      const response = await getChatBookmarks();
      this.bookmarks = response.body;
      this.#updateBookmarksList();
    } catch (error) {
      console.error('Error refreshing bookmarks:', error);
      toast.error(
        i18next.t('sidebar.aiAnalyzer.toast.failed_to_load_bookmarks'),
      );
    }
  }

  #updateBookmarksList() {
    const listContainer = this.find('#bookmarks-list')[0];
    if (!listContainer) return;

    listContainer.clear();

    const filteredBookmarks = this.bookmarks.filter(bookmark =>
      bookmark.message.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );

    if (filteredBookmarks.length === 0) {
      const emptyState = new UIText(
        this.searchQuery
          ? i18next.t('sidebar.aiAnalyzer.no_search_results')
          : i18next.t('sidebar.aiAnalyzer.bookmarks_empty'),
      );
      emptyState.setClass('text-gray-500 text-center mt-4');
      listContainer.add(emptyState);
      return;
    }

    filteredBookmarks.forEach(bookmark => {
      const bookmarkItem = new UIDiv();
      bookmarkItem.setClass(
        'border-b border-gray-200 py-4 px-2 hover:bg-gray-50 hover:cursor-pointer',
      );

      bookmarkItem.onClick(e => {
        e.stopPropagation();
        this.copyBookmark(bookmark.message);
      });

      const contentWrapper = new UIDiv();
      contentWrapper.setClass('flex justify-between items-center');

      const content = new UIText(bookmark.message);
      content.setClass('text-lg font-medium mb-2 line-clamp-3');
      contentWrapper.add(content);

      // Add remove from saved button
      const removeButton = new UIButton();
      removeButton.setClass('ml-2 p-1 hover:bg-gray-100 rounded-md');
      const removeIcon = new TrashIcon({
        size: 16,
        color: 'none',
        strokeColor: 'red',
      });
      removeButton.add(removeIcon);
      removeButton.onClick(async e => {
        e.stopPropagation();
        const modal = new Modal('', {
          description: i18next.t('sidebar.aiAnalyzer.remove_bookmark_confirm'),
          cancelText: i18next.t('common.cancel'),
          confirmText: i18next.t('common.delete'),
          confirmClass:
            'bg-[var(--o-hive-error-80)] text-white hover:enabled:bg-[var(--o-hive-error-100)]',
          handleOnAction: async () => {
            await deleteChatBookmark(bookmark.id);
            this.bookmarks = this.bookmarks.filter(b => b.id !== bookmark.id);
            this.#updateBookmarksList();
          },
        });

        await modal.show();
      });
      contentWrapper.add(removeButton);

      const metadata = new UIDiv();
      metadata.setClass(
        'flex justify-between items-center mt-2 text-sm text-gray-600',
      );

      const createdAt = new UIText(getDateDisplay(bookmark.createdAt));
      metadata.add(createdAt);

      bookmarkItem.add(contentWrapper);
      bookmarkItem.add(metadata);

      listContainer.add(bookmarkItem);
    });
  }

  #buildComponent() {
    const search = new Search({
      handleSearch: searchValue => {
        this.searchQuery = searchValue;
        this.#updateBookmarksList();
      },
      className: 'my-2 px-1',
      placeholder: i18next.t('common.search'),
    });
    this.add(search);

    const listContainer = new UIDiv();
    listContainer.setId('bookmarks-list');
    listContainer.setClass('flex-1 overflow-y-auto');
    this.add(listContainer);
  }
}

export { AiAnalyzerBookmarks };
